import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useSelectedOrganization } from '../../hooks/useSelectedOrganization';
import { Organization } from '../../types/organization';

/**
 * Компонент селектора организации
 * Отображает выпадающий список доступных организаций с состояниями загрузки
 */
export const OrgSelector: React.FC = () => {
  const { data: organizations = [], isLoading, error } = useOrganizations();
  const { selectedOrganization, setSelectedOrganization, isInitialized } = useSelectedOrganization();

  const handleChange = (event: any) => {
    const selectedOrgId = event.target.value;
    
    if (!selectedOrgId) {
      setSelectedOrganization(null);
      return;
    }

    const selectedOrg = organizations.find(
      (org: Organization) => org.organization_id === selectedOrgId
    );
    
    if (selectedOrg) {
      setSelectedOrganization(selectedOrg);
    }
  };

  // Показываем загрузку пока не завершена инициализация
  if (isLoading || !isInitialized) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        minWidth: { xs: 120, sm: 200 },
        justifyContent: { xs: 'center', sm: 'flex-start' }
      }}>
        <CircularProgress size={20} sx={{ mr: { xs: 0, sm: 1 }, color: 'rgba(255, 255, 255, 0.7)' }} />
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'rgba(255, 255, 255, 0.7)', 
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            display: { xs: 'none', sm: 'block' }
          }}
        >
          Загрузка организаций...
        </Typography>
      </Box>
    );
  }

  // Показываем ошибку если не удалось загрузить
  if (error) {
    return (
      <Box sx={{ minWidth: { xs: 120, sm: 200 } }}>
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'error.light', 
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            textAlign: { xs: 'center', sm: 'left' }
          }}
        >
          {window.innerWidth < 600 ? 'Ошибка' : 'Ошибка загрузки организаций'}
        </Typography>
      </Box>
    );
  }

  // Показываем placeholder если список пуст
  if (organizations.length === 0) {
    return (
      <Box sx={{ minWidth: { xs: 120, sm: 200 } }}>
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'rgba(255, 255, 255, 0.7)', 
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            textAlign: { xs: 'center', sm: 'left' }
          }}
        >
          {window.innerWidth < 600 ? 'Нет организаций' : 'Нет доступных организаций'}
        </Typography>
      </Box>
    );
  }

  return (
    <FormControl 
      size="small" 
      sx={{ 
        minWidth: { xs: 120, sm: 200 },
        maxWidth: { xs: 150, sm: 280 },
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
          borderColor: 'rgba(255, 255, 255, 0.23)',
          color: 'white',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
            borderColor: 'rgba(255, 255, 255, 0.4)',
          },
          '&.Mui-focused': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            borderColor: 'primary.main',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
            },
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.23)',
          },
        },
        '& .MuiInputLabel-root': {
          fontSize: { xs: '0.75rem', sm: '0.875rem' },
          color: 'rgba(255, 255, 255, 0.7)',
          '&.Mui-focused': {
            color: 'primary.main',
          },
          display: { xs: 'none', sm: 'block' },
        },
        '& .MuiSelect-select': {
          fontSize: { xs: '0.75rem', sm: '0.875rem' },
          paddingY: { xs: 0.5, sm: 1 },
          color: 'white',
        },
        '& .MuiSelect-icon': {
          color: 'rgba(255, 255, 255, 0.7)',
        },
      }}
      disabled={isLoading}
    >
      <InputLabel id="org-selector-label">Организация</InputLabel>
      <Select
        labelId="org-selector-label"
        id="org-selector"
        value={selectedOrganization.orgId || ''}
        label="Организация"
        onChange={handleChange}
        displayEmpty
        renderValue={(value) => {
          if (!value) return 'Организация';
          const org = organizations.find((o: Organization) => o.organization_id === value);
          const orgName = org?.organization_name || '';
          // На мобильных показываем сокращенное название
          return window.innerWidth < 600 && orgName.length > 15 
            ? `${orgName.substring(0, 12)}...` 
            : orgName;
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              bgcolor: 'background.paper',
              mt: 1,
              '& .MuiMenuItem-root': {
                fontSize: '0.875rem',
              },
            },
          },
        }}
      >
        {organizations.map((org: Organization) => (
          <MenuItem 
            key={org.organization_id} 
            value={org.organization_id}
            sx={{
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              },
            }}
          >
            {org.organization_name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}; 